<template lang="pug">
.google-review-listing
  template(v-if="isFetching")
    v-row(no-gutters)
      v-col(cols="8")
        v-skeleton-loader(type="article@3")
  template(v-else)
    template(v-if="totalCount === 0")
      listing-empty-state.my-4(v-if="hasFiltersOrSearch" iconName="$yzi-star-01" :primaryText="$t('item_not_found', { item:'google review' })" :secondaryText="$t('item_not_found_description')")
      listing-empty-state.my-4(v-else iconName="$yzi-star-01" :primaryText="$t('google_review_listing_empty_primary_text')" :secondaryText="$t('google_review_listing_empty_secondary_text')" :btnText="$t('send_review_invite')" @btnClicked="$emit('toInvitationTab')")
    template(v-else)
      v-row(no-gutters)
        v-col(cols="12" sm="8" md="6")
          listing-item.my-4(v-for="review in reviews" :key="review.id" :os="os" responseType="googleReview" :name="review.reviewer_name" :profilePhoto="review.reviewer_photo_url" :dateTime="convertDateTime(review.posted_at)" :star="review.star" :comment="review.comment" :response="review.reply_comment" :rightBtnText="review.reply_comment ? $t('replied') : $t('reply_review')" :rightBtnDisabled="review.reply_comment !== null" :leftBtnText="getLeftBtnText(review)" :overrideBottomView="isShowingBottomView && selectedReview === review" :existingCustomerBadge="review.customer !== null" @leftBtnClicked="assignOrViewCustomer(review)" :isEditingResponse="isShowingBottomView && selectedReview === review" @rightBtnClicked="showBottomView(review)" :leftBtnTooltipText="review.customer ? null : $t('assign_to_item_tooltip_text', { item:itemText })" @deleteResponse="selectedReview = review; openDeleteResponseDialog = true" @editResponse="showBottomView(review)")
            template(#bottomView)
              reply-item(v-if="isShowingBottomView && selectedReview === review" :title="reviewAccount.account_name" :placeholder="$t('your_response')" :currentReply="review.reply_comment" :rightBtnText="$t('save')" :rightBtnLoading="isSavingResponse" :leftBtnText="$t('cancel')" @leftBtnClicked="resetData()" @rightBtnClicked="saveResponse" replyType="googleReview")
            template(v-if="review.customer !== null" #itemAction)
              v-menu(left :offset-x='true')
                template(v-slot:activator="{ on, attrs }")
                  v-btn.mr-2(icon v-on="on" small)
                    v-icon(color="basic700" size="17") mdi-dots-vertical
                v-list(dense)
                  v-list-item(@click="reassignCustomer(review)")
                    v-list-item-title.s2.basic800--text {{ $t('reassign_to_another_item', { item:itemText }) }}
                  v-list-item(@click="unassignCustomer(review)")
                    v-list-item-title.s2.danger--text {{ $t('unassign_this_item_from_review', { item:itemText }) }}
      div.d-flex.align-center.mb-4(v-if="totalCount > pageSize")
        v-spacer
        div
          yc-pagination-custom(v-model="currentPage" :parentPageSize.sync="pageSize" :count="totalCount" text="Item list" attach menu-top @input="pageChanged")
  .dialogs
    delete-confirmation-dialog(v-if="openDeleteResponseDialog" :open.sync="openDeleteResponseDialog" :isLoading="isDeletingResponse" @cancel="resetData(); openDeleteResponseDialog = false" @delete="deleteResponse")
      .text-lg.font-medium {{ $t('delete_confirmation', { text: $t('google_review_response').toLowerCase() }) }}
      .text-md.mt-4 {{ $t('review_delete_confirmation_description_primary', { text: $t('google_review_response') }) }}             
      .text-md {{ $t('review_delete_confirmation_description_secondary') }}
    review-assign-customer-dialog(v-if="openAssignCustomerDialog" :open.sync="openAssignCustomerDialog" :parentReview="selectedReview" :os="os" @close="resetData()" @update="updateReview")
    review-unassign-customer-dialog(v-if="openUnassignCustomerDialog" :open.sync="openUnassignCustomerDialog" :parentReview="selectedReview" :os="os" @close="resetData()" @update="updateReview")
</template>

<script>
import { DateTime } from 'luxon'
import { mapGetters, mapActions } from 'vuex'
import ListingEmptyState from '@/components/review/listing/ListingEmptyState.vue'
import ListingItem from '@/components/review/listing/ListingItem.vue'
import ReplyItem from '@/components/review/listing/ReplyItem.vue'
import DeleteConfirmationDialog from '@/components/review/dialogs/DeleteConfirmationDialog.vue'
import ReviewAssignCustomerDialog from '@/components/review/dialogs/ReviewAssignCustomerDialog.vue'
import ReviewUnassignCustomerDialog from '@/components/review/dialogs/ReviewUnassignCustomerDialog.vue'

export default {
  name: 'GoogleReviewListing',
  components: {
    ListingEmptyState,
    ListingItem,
    ReplyItem,
    DeleteConfirmationDialog,
    ReviewAssignCustomerDialog,
    ReviewUnassignCustomerDialog
  },
  props: {
    searchText: {
      type: String,
      default: ''
    },
    filters: {
      type: Array,
      default: () => {
        return []
      }
    },
    os: {
      type: String,
      default: 'review',
      validator(value) {
        return ['clinic', 'donation', 'connect', 'review'].includes(value)
      }
    },
  },
  data() {
    return {
      isFetching: false,
      totalCount: 0,
      pageSize: 20,
      currentPage: 1,
      reviews: [],
      selectedReview: null,
      isShowingBottomView: false,
      isSavingResponse: false,
      isDeletingResponse: false,
      openDeleteResponseDialog: false,
      openAssignCustomerDialog: false,
      openUnassignCustomerDialog: false
    }
  },
  watch: {
    pageSize() {
      this.fetchGoogleReview()
    },
    filters() {
      this.fetchGoogleReview()
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      reviewAccount: 'review/getMainAccount'
    }),
    hasFiltersOrSearch() {
      return this.filters.length > 0 || this.searchText.length > 0
    },
    itemText() {
      switch (this.os) {
        case 'clinic':
          return this.$t('patient').toLowerCase()

        case 'review':
          return this.$t('customer').toLowerCase()
      
        default:
          return ''
      }
    }
  },
  async mounted () {
    this.isFetching = true
    await this.fetchGoogleReview()
    this.isFetching = false
  },
  methods: {
    ...mapActions(['showMessage']),
    pageChanged(num) {
      this.fetchGoogleReview(num)
    },
    convertDateTime(value) {
      const isoDateTime = DateTime.fromISO(value)
      const date = isoDateTime.toFormat('dd MMM yyyy')
      const time = isoDateTime.toFormat('hh:mm a')
      return `${date}, ${time}`
    },
    showBottomView(review) {
      this.selectedReview = review
      this.isShowingBottomView = true
    },
    async fetchGoogleReview(page) {
      const params = {
        expand: 'customer',
        search: this.searchText,
        page: page || 1,
        page_size: this.pageSize,
        ordering: '-posted_at'
      }
      if (this.filters.length > 0) {
        this.filters.map(filter => {
          if (filter.value) {
            params[filter.key] = filter.value
          }
        })
      }
      try {
        const { data } = await this.$axios.get(`/reviews/${this.company.slug}/google_reviews`, { params })
        this.reviews = data.results
        this.totalCount = data.count
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      }
    },
    async saveResponse(value) {
      this.isSavingResponse = true
      try {
        const { data } = await this.$axios.patch(`/reviews/${this.company.slug}/google_reviews/${this.selectedReview.id}`, { reply_comment: value })
        this.updateReview(data)
        this.showMessage(['success', this.$t('success_message.successfully_updated')])
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.resetData()
      }
    },
    async deleteResponse() {
      this.isDeletingResponse = true
      try {
        const { data } = await this.$axios.patch(`/reviews/${this.company.slug}/google_reviews/${this.selectedReview.id}`, { reply_comment: null })
        this.updateReview(data)
        this.showMessage(['success', this.$t('success_message.delete_successful')])
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.openDeleteResponseDialog = false
        this.resetData()
      }
    },
    updateReview(review) {
      const findIndex = this.reviews.findIndex((item) => item.id === review.id)
      if (findIndex >= 0) {
        this.reviews.splice(findIndex, 1, review)
      }
    },
    assignOrViewCustomer(review) {
      if (review.customer) {
        switch (this.os) {
          case 'review':
          case 'clinic':
            this.$emit('showCustomerDetails', review.customer)
            return;
        
          default:
            return;
        }
      }
      this.selectedReview = review
      this.openAssignCustomerDialog = true
    },
    reassignCustomer(review) {
      this.selectedReview = review
      this.openAssignCustomerDialog = true
    },
    unassignCustomer(review) {
      this.selectedReview = review
      this.openUnassignCustomerDialog = true
    },
    resetData() {
      this.selectedReview = null
      this.isShowingBottomView = false
      this.isSavingResponse = false
      this.isDeletingResponse = false
    },
    getLeftBtnText(review) {
      return review.customer ?
        this.$t('google_review_view_item_details', { item:this.os === 'review' ? this.$t('customer').toLowerCase() : this.$t('patient').toLowerCase() }) :
        this.$t('google_review_assign_item', { item:this.os === 'review' ? this.$t('customer').toLowerCase() : this.$t('patient').toLowerCase() })
    }
  },
}
</script>

<style scoped>
</style>
